import {
  EditType,
  type HighSchoolClassYearProfileEdit,
  type HighSchoolProfileEdit,
  type HometownProfileEdit,
  type NcaaIdProfileEdit,
  type VitalsProfileEdit,
} from '@on3/api';
import { Form } from '@on3/ui-lib/components/Form';
import { externalApi, useToast } from '@on3/ui-lib/index';
import { isDiff } from '@on3/ui-lib/utils/compare';
import { updatePath } from '@on3/ui-lib/utils/updatePath';
import { yup } from '@on3/ui-lib/utils/yup';
import { type IDetailsRequest } from '@public/components/Elite/Profile/Edits/Details';
import { DetailsHometown } from '@public/components/Elite/Profile/Edits/Details/Partials/Hometown';
import { DetailsNcaa } from '@public/components/Elite/Profile/Edits/Details/Partials/Ncaa';
import { DetailsOrganization } from '@public/components/Elite/Profile/Edits/Details/Partials/Organization';
import { DetailsVitals } from '@public/components/Elite/Profile/Edits/Details/Partials/Vitals';
import { usePlayerProfile } from '@public/contexts/PlayerProfile/PlayerProvider';
import { getProfileUpdates } from '@public/utils/player/admin';
import { createContext, useCallback, useState } from 'react';

import styles from './DetailsModal.module.scss';

type ProfileDetailsEditType =
  | NcaaIdProfileEdit
  | VitalsProfileEdit
  | HighSchoolClassYearProfileEdit
  | HighSchoolProfileEdit
  | HometownProfileEdit;

const detailSchema = yup.object().shape({
  ncaaId: yup.object().shape({
    value: yup
      .string()
      .length(10, 'NCAA ID must be 10 characters')
      .transform((v) => v || null)
      .nullable(),
  }),
  height: yup.object().shape({
    value: yup
      .string()
      .test(
        'min',
        'Height must be at least 4 feet.',
        (value) => !value || Number(value) >= 48,
      )
      .test(
        'max',
        'Height cannot exceed 8 feet.',
        (value) => !value || Number(value) <= 96,
      )
      .nullable()
      .label('Height'),
  }),
  weight: yup.object().shape({
    value: yup
      .string()
      .nullable()
      .test(
        'min',
        'Weight must be at least 80 lbs.',
        (value) => !value || Number(value) >= 80,
      )
      .test(
        'max',
        'Weight cannot exceed 500 lbs.',
        (value) => !value || Number(value) <= 500,
      )
      .label('Weight'),
  }),
  hsClassYear: yup.object().shape({
    value: yup.number().required().label('Class Year'),
  }),
  highSchool: yup.object().shape({
    organizationName: yup.string().nullable(),
    organizationKey: yup.number().nullable(),
  }),
  hometown: yup.object().shape({
    hometownCityKey: yup.number().nullable(),
  }),
});

export const ProfileDetailsContext = createContext<{
  values: IDetailsRequest;
  errors: Record<string, string>;
  handleUpdate: (key: KeyPath<IDetailsRequest>, val: any) => void;
}>({
  values: {
    ncaaId: {
      $type: 'ncaaId',
      editType: EditType.Add,
      value: '',
    },
    height: {
      $type: 'vitals',
      editType: EditType.Add,
      measurementTypeKey: 1,
      value: '',
    },
    weight: {
      $type: 'vitals',
      editType: EditType.Add,
      measurementTypeKey: 2,
      value: '',
    },
    hsClassYear: {
      $type: 'hsClassYear',
      editType: EditType.Add,
      value: null,
    },
    highSchool: {
      $type: 'highSchool',
      editType: EditType.Add,
      organizationKey: null,
      organizationName: '',
    },
    hometown: {
      $type: 'hometown',
      editType: EditType.Add,
      hometownCityKey: null,
      hometownCityName: '',
    },
  },
  errors: {},
  handleUpdate: () => {},
});

interface IDetailsModalProps {
  details: IDetailsRequest;
  onComplete: (d: Partial<IDetailsRequest>) => void;
}

export const DetailsModal = ({
  details,
  onComplete = () => {},
}: IDetailsModalProps) => {
  const {
    state: { player },
  } = usePlayerProfile();
  const { notify } = useToast();

  const [values, setValues] = useState(details);

  const handleUpdate = useCallback(
    (key: KeyPath<IDetailsRequest>, val: any) =>
      setValues((curr) => updatePath(curr, key, val)),
    [],
  );

  const handleSubmit = useCallback(
    async (data: IDetailsRequest) => {
      try {
        const updates = getProfileUpdates(
          externalApi,
          { ...data },
          details,
          {},
          player.key,
        );

        const responses = await Promise.allSettled(updates.map((fn) => fn()));

        onComplete(
          responses.reduce((acc, curr, i) => {
            if (curr.status === 'fulfilled') {
              const edit = curr.value.reviewItem
                .newValue as ProfileDetailsEditType;

              let reqKey: keyof IDetailsRequest;

              if (edit.$type === 'vitals') {
                const vitalEdit = edit as VitalsProfileEdit;

                reqKey =
                  vitalEdit.measurementTypeKey === 1 ? 'height' : 'weight';
                acc[reqKey] = vitalEdit;
              }

              if (edit.$type === 'ncaaId') {
                reqKey = 'ncaaId';
                acc[reqKey] = edit as NcaaIdProfileEdit;
              }

              if (edit.$type === 'highSchool') {
                reqKey = 'highSchool';
                acc[reqKey] = edit as HighSchoolProfileEdit;
              }

              if (edit.$type === 'hsClassYear') {
                reqKey = 'hsClassYear';
                acc[reqKey] = edit as HighSchoolClassYearProfileEdit;
              }

              if (edit.$type === 'hometown') {
                reqKey = 'hometown';
                acc[reqKey] = edit as HometownProfileEdit;
              }
            }

            return acc;
          }, {} as Partial<IDetailsRequest>),
        );

        if (responses.every((r) => r.status === 'rejected')) {
          notify(
            'Failed to update profile details. Please refresh and try again.',
            {
              type: 'error',
            },
          );

          return;
        }

        if (responses.some((r) => r.status === 'rejected')) {
          notify(
            'Some profile updates failed to submit. Please refresh and try again.',
            {
              type: 'warning',
            },
          );

          return;
        }

        notify('Profile updates submitted successfully', { type: 'success' });
      } catch (error) {
        notify('Failed to update profile details. Please try again.', {
          type: 'error',
        });
      }
    },
    [details, notify, onComplete, player.key],
  );

  return (
    <div data-ui="modal">
      <Form
        className={styles.block}
        onSubmit={handleSubmit}
        schema={detailSchema}
        values={values}
      >
        {({ processing, errors }) => (
          <ProfileDetailsContext.Provider
            value={{
              errors,
              values,
              handleUpdate,
            }}
          >
            <h2>Edit Details</h2>
            <div className={styles.fields}>
              <DetailsNcaa />
              <DetailsVitals />
              <DetailsOrganization />
              <DetailsHometown />
            </div>
            <button
              disabled={processing || !isDiff(details, values)}
              type="submit"
            >
              Save
            </button>
          </ProfileDetailsContext.Provider>
        )}
      </Form>
    </div>
  );
};
